import React, { useCallback, useEffect } from 'react';
import { Row, Col, Modal as BootstrapModal } from 'react-bootstrap';
import { Alert, Button, FormInput, FormLabel } from 'modules';
import { InstallmentsData, InstallmentsType } from 'constants/Installments';
import { PriceModel } from 'helpers/price/priceModel';
import InstallmentsPlanTable from 'components/InstallmentsPlanTable';

interface InstallmentsModalProps {
  priceModel: PriceModel;
  data: InstallmentsData | null;
  onChange: (data: InstallmentsData) => void;
  onSubmit: (data: GQL.IInstallmentsPlan) => void;
  onClose: () => void;
}

const InstallmentsModal = ({ priceModel, data, onChange, onSubmit, onClose }: InstallmentsModalProps) => {
  const handleClose = (e: React.MouseEvent) => {
    onClose();
    e.stopPropagation();
    e.preventDefault();
  };

  const isValid = useCallback(() => {
    return (
      data?.type &&
      data?.firstPayment >= (priceModel?.installments?.minimalFirstPayment || 90) &&
      data?.paymentsCount >= 2 &&
      data?.paymentsCount <= 6
    );
  }, [data, priceModel]);

  const getInstallmentsTypeOptions = useCallback(() => {
    const options = [];
    if (priceModel.installments.canFixed) {
      options.push({
        label: 'Fixná platba',
        value: InstallmentsType.FIXED,
      });
    }
    if (priceModel.installments.canEqually) {
      options.push({
        label: 'Rovnomerné splátky',
        value: InstallmentsType.EQUALLY,
      });
    }
    return options;
  }, [priceModel]);

  const getMonthLabel = useCallback((count: number) => (count >= 5 ? 'mesiacov' : 'mesiace'), []);

  const getPaymentsPlanOptions = useCallback(() => {
    const move = data?.type === InstallmentsType.FIXED ? 1 : 0;
    const options = [];
    for (let i = 2; i <= 6; i++) {
      options.push({
        value: i,
        label: `${i + move} ${getMonthLabel(i)}`,
      });
    }
    return options;
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (!data) {
      onChange({
        type: InstallmentsType.FIXED,
        paymentsCount: 2,
        firstPayment: priceModel.installments.minimalFirstPayment || 90,
      });
    }
    // eslint-disable-next-line
  }, [data, priceModel]);

  const onModalKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
    }
  };

  if (!data) {
    return null;
  }

  return (
    <BootstrapModal
      show
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      animation={false}
      dialogClassName="modal-80w max-w-500"
    >
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title>Úhrada na splátky</BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body onKeyDown={onModalKeyDown}>
        <Row>
          <Col sm={8}>
            <FormInput
              type="select"
              name="type"
              label="Typ splátek"
              value={data.type}
              options={getInstallmentsTypeOptions()}
              onChange={(e: any) => {
                onChange({
                  ...data,
                  type: e.target.value as InstallmentsType,
                });
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <FormInput
              name="firstPayment"
              label="1. platba"
              required
              disabled={data.type !== InstallmentsType.FIXED}
              value={data.type === InstallmentsType.FIXED ? data.firstPayment : ''}
              onChange={(e: any) => {
                onChange({
                  ...data,
                  firstPayment: Number(e.target.value),
                });
              }}
            />
          </Col>
          <Col sm={6}>
            <FormInput
              type="select"
              name="paymentsCount"
              label="Počet splátok"
              required
              value={data.paymentsCount}
              options={getPaymentsPlanOptions()}
              onChange={(e: any) => {
                onChange({
                  ...data,
                  paymentsCount: Number(e.target.value),
                });
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            {priceModel?.installments?.error ? (
              <Alert type="danger" canClose={false} className="mt-3">
                Parametre splátok nie sú správne
              </Alert>
            ) : (
              <div className="mt-2">
                <FormLabel>Prehľad splátok</FormLabel>
                <InstallmentsPlanTable plan={priceModel?.installments} />
              </div>
            )}
          </Col>
        </Row>
      </BootstrapModal.Body>
      <BootstrapModal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Zavrieť
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={!isValid()}
          onClick={() => {
            if (isValid()) {
              onSubmit({
                price: priceModel.installments.price,
                paymentsPlan: priceModel.installments.paymentsPlan,
              });
            }
          }}
        >
          Nastaviť splátky
        </Button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
};

export default InstallmentsModal;
